import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  confirmSignUp,
  resendConfirmationCode,
  signIn,
  signUp,
  startHostedUiFlow,
} from './cognito';
import { useAuth } from './AuthContext';
import { JotlyMark } from '../components/JotlyMark';

// Two-step inline flow: (1) collect email + password + name → call SignUp;
// (2) collect the 6-digit code → ConfirmSignUp, then auto-sign-in and route
// home. Self-contained so this file can be deleted if signups are ever closed.
type Stage =
  | { kind: 'details' }
  | { kind: 'confirm'; email: string; password: string };

export function SignUpScreen() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [stage, setStage] = useState<Stage>({ kind: 'details' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await signUp({ email, password, name });
      setStage({ kind: 'confirm', email, password });
    } catch (err) {
      const message = (err as Error).message || '';
      // Cognito returns "User already exists" when the email matches an
      // existing account. That can mean either (a) the account is fully
      // confirmed — they should sign in — or (b) a previous signup created
      // the account but the user never entered the code. We can't distinguish
      // without the password, so we ask Cognito to resend a fresh code: if
      // the account is unconfirmed it sends one; if confirmed it errors and
      // the catch shows the "sign in" path.
      if (/already exists/i.test(message)) {
        try {
          await resendConfirmationCode(email);
          setInfo("There's already an account for this email but it wasn't confirmed — we sent a fresh code.");
          setStage({ kind: 'confirm', email, password });
        } catch (resendErr) {
          const resendMessage = (resendErr as Error).message || '';
          // "User is already confirmed" is the clean "go sign in" path.
          if (/already confirmed/i.test(resendMessage)) {
            setError('That email is already registered. Try signing in instead.');
          } else {
            setError(`That email is already registered. ${resendMessage || 'Try signing in.'}`);
          }
        }
      } else {
        setError(message || 'Could not create your account.');
      }
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.kind !== 'confirm') return;
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await confirmSignUp(stage.email, code.trim());
      // Sign the user in straight away — PostConfirmation has bootstrapped
      // the profile and (best-effort) emailed the admin. Account is APPROVED
      // by default; admin can block later via the email link.
      const result = await signIn(stage.email, stage.password);
      if (result.kind === 'ok') {
        setAuthed(true);
        navigate('/', { replace: true });
        return;
      }
      // NEW_PASSWORD_REQUIRED is impossible right after self-signup — the user
      // chose the password themselves. Fall through to a defensive message.
      setError('Account created, but auto sign-in failed — please log in manually.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Could not confirm your account.');
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (stage.kind !== 'confirm') return;
    setError(null);
    setBusy(true);
    try {
      await resendConfirmationCode(stage.email);
      setInfo('We sent a fresh code — check your inbox.');
    } catch (err) {
      setError((err as Error).message || 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  // Federated sign-up reuses the same Hosted UI flow as sign-in: Cognito's
  // PreSignUp trigger auto-creates the native user record on first encounter,
  // so there's no separate "register" code path for social — one button does
  // both. We keep the buttons on the signup screen for discoverability.
  const onSocial = (provider: 'Google' | 'SignInWithApple' | 'Facebook') => async () => {
    setError(null);
    try {
      await startHostedUiFlow(provider);
    } catch (err) {
      setError((err as Error).message || 'Could not start sign-up.');
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <JotlyMark size={32} />
          <span style={brandStyle}>Jotly</span>
        </div>
        <h1 style={headingStyle}>
          {stage.kind === 'details' ? 'Create your account.' : 'Check your email.'}
        </h1>

        {stage.kind === 'details' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
              <button type="button" onClick={onSocial('SignInWithApple')} style={socialButtonStyle}>
                Sign up with Apple
              </button>
              <button type="button" onClick={onSocial('Google')} style={socialButtonStyle}>
                Sign up with Google
              </button>
              <button type="button" onClick={onSocial('Facebook')} style={socialButtonStyle}>
                Sign up with Facebook
              </button>
            </div>
            <div style={dividerStyle}>
              <span style={dividerLineStyle} />
              <span style={dividerTextStyle}>or with email</span>
              <span style={dividerLineStyle} />
            </div>
          <form onSubmit={onSignUp} style={formStyle}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                required
                autoComplete="email"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Name (optional)</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                style={inputStyle}
              />
            </label>
            <p style={hintStyle}>
              At least 8 characters, including a number and a lowercase letter.
              We'll email you a code to verify your address.
            </p>
            <p style={hintStyle}>
              New accounts may be reviewed and blocked by the admin if anything
              looks off — by signing up you agree this is fine.
            </p>
            <button type="submit" disabled={busy} style={primaryButtonStyle}>
              {busy ? 'Creating…' : 'Create account'}
            </button>
          </form>
          </>
        )}

        {stage.kind === 'confirm' && (
          <form onSubmit={onConfirm} style={formStyle}>
            <p style={hintStyle}>
              We sent a 6-digit code to <strong style={{ color: 'var(--rc-ink)' }}>{stage.email}</strong>.
              Paste it below to finish.
            </p>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Confirmation code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                style={inputStyle}
              />
            </label>
            <button type="submit" disabled={busy} style={primaryButtonStyle}>
              {busy ? 'Confirming…' : 'Confirm & sign in'}
            </button>
            <button type="button" onClick={onResend} disabled={busy} style={linkButtonStyle}>
              Resend the code
            </button>
          </form>
        )}

        {info && <div role="status" style={infoStyle}>{info}</div>}
        {error && <div role="alert" style={errorStyle}>{error}</div>}

        <button
          type="button"
          onClick={() => navigate('/login')}
          style={{ ...linkButtonStyle, marginTop: 16 }}
        >
          Already have an account? Sign in
        </button>
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'var(--rc-card)',
  borderRadius: 18,
  padding: '34px 32px 28px',
  boxShadow: '0 12px 36px rgba(20,22,27,0.06), 0 1px 0 rgba(20,22,27,0.03)',
  border: '1px solid var(--rc-border)',
};

const brandStyle: React.CSSProperties = {
  font: '700 18px/1 var(--rc-display)',
  letterSpacing: '-0.01em',
  color: 'var(--rc-ink)',
};

const headingStyle: React.CSSProperties = {
  font: '700 28px/1.1 var(--rc-display)',
  margin: '0 0 24px',
  letterSpacing: '-0.02em',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelTextStyle: React.CSSProperties = {
  font: '500 12.5px/1 var(--rc-mono)',
  color: 'var(--rc-ink-2)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid var(--rc-border-2)',
  background: 'var(--rc-card-2)',
  font: '500 15px/1.2 var(--rc-sans)',
  color: 'var(--rc-ink)',
  outline: 'none',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: 4,
  appearance: 'none',
  border: 0,
  cursor: 'pointer',
  padding: '12px 16px',
  borderRadius: 12,
  background: 'var(--rc-ink)',
  color: '#fff',
  font: '600 14px/1 var(--rc-sans)',
};

const linkButtonStyle: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  padding: '8px 4px',
  color: 'var(--rc-ink-2)',
  font: '500 13px/1 var(--rc-sans)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  alignSelf: 'center',
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--rc-danger-soft)',
  color: 'var(--rc-danger)',
  font: '500 13px/1.4 var(--rc-sans)',
};

const infoStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--rc-card-2)',
  color: 'var(--rc-ink-2)',
  font: '500 13px/1.4 var(--rc-sans)',
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  font: '400 13.5px/1.5 var(--rc-sans)',
  color: 'var(--rc-ink-2)',
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  margin: '20px 0 12px',
};

const dividerLineStyle: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--rc-border)',
};

const dividerTextStyle: React.CSSProperties = {
  font: '500 11px/1 var(--rc-mono)',
  color: 'var(--rc-ink-3)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const socialButtonStyle: React.CSSProperties = {
  appearance: 'none',
  cursor: 'pointer',
  padding: '11px 14px',
  borderRadius: 12,
  border: '1px solid var(--rc-border-2)',
  background: 'var(--rc-card-2)',
  color: 'var(--rc-ink)',
  font: '600 13.5px/1 var(--rc-sans)',
};
